import type { HealthStatus } from './types';

// ── Types ────────────────────────────────────────────────────────────────────

export type EstimationDisplay = 'STORY_POINTS' | 'EU_RU' | 'BOTH';

interface SprintHealthPanelProps {
  sprintDay:    number;
  sprintTotal:  number;
  ptsDone:      number;
  ptsRemaining: number;
  spikeCount:   number;
  spikePts:     number;
  healthStatus: HealthStatus;
  totalPoints:  number;
  // #3605 — not read: done/remaining are summed story points for every type,
  // so they are labelled points (D12). Kept for BoardPage; PR-2's display
  // sweep decides what each type adds.
  estimationType?: EstimationDisplay;
}

export interface BurndownPoint {
  day:    number;
  ideal:  number;
  actual: number | null;
}

// ── Burndown data generator ──────────────────────────────────────────────────

export function generateBurndownData(
  totalPoints: number,
  sprintTotal: number,
  ptsDone: number,
  sprintDay: number,
): BurndownPoint[] {
  if (sprintTotal === 0) return [];

  const points: BurndownPoint[] = [];
  const idealRate = totalPoints / sprintTotal;

  for (let d = 0; d <= sprintTotal; d++) {
    const ideal = Math.max(0, totalPoints - idealRate * d);
    const actual = d <= sprintDay ? null : null; // placeholder
    points.push({ day: d, ideal, actual });
  }

  // Fill actual up to current day
  // Simple linear interpolation from totalPoints to remaining
  const remaining = totalPoints - ptsDone;
  for (let d = 0; d <= Math.min(sprintDay, sprintTotal); d++) {
    const t = sprintDay > 0 ? d / sprintDay : 0;
    points[d].actual = totalPoints - (totalPoints - remaining) * t;
  }

  return points;
}

// ── Mini SVG Burndown Chart ──────────────────────────────────────────────────

function MiniChart({ data, totalPoints }: { data: BurndownPoint[]; totalPoints: number }) {
  if (data.length === 0) return null;

  const W = 200;
  const H = 80;
  const pad = 8;

  const maxDay = data[data.length - 1].day;
  const maxPts = totalPoints || 1;

  const toX = (day: number) => pad + ((day / maxDay) * (W - 2 * pad));
  const toY = (pts: number) => pad + ((1 - pts / maxPts) * (H - 2 * pad));

  // Ideal line
  const idealPoints = data
    .map(d => `${toX(d.day)},${toY(d.ideal)}`)
    .join(' ');

  // Actual line (only non-null)
  const actualData = data.filter(d => d.actual !== null);
  const actualPoints = actualData
    .map(d => `${toX(d.day)},${toY(d.actual!)}`)
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      role="img"
      aria-label="Sprint burndown chart"
      data-testid="burndown-chart"
    >
      {/* Ideal line — grey dashed */}
      <polyline
        points={idealPoints}
        fill="none"
        stroke="#9ca3af"
        strokeWidth="1.5"
        strokeDasharray="4 3"
      />
      {/* Actual line — purple solid */}
      {actualPoints && (
        <polyline
          points={actualPoints}
          fill="none"
          stroke="#7c3aed"
          strokeWidth="2"
        />
      )}
    </svg>
  );
}

// ── Health Badge ─────────────────────────────────────────────────────────────

const HEALTH_COLORS: Record<HealthStatus, { bg: string; text: string; dot: string }> = {
  GREEN: { bg: 'rgba(34,197,94,0.1)', text: '#16a34a', dot: '#22c55e' },
  AMBER: { bg: 'rgba(245,158,11,0.1)', text: '#d97706', dot: '#f59e0b' },
  RED:   { bg: 'rgba(239,68,68,0.1)',  text: '#dc2626', dot: '#ef4444' },
};

function HealthBadge({ status }: { status: HealthStatus }) {
  const c = HEALTH_COLORS[status];
  return (
    <span
      data-testid={`health-badge-${status.toLowerCase()}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 9999,
        backgroundColor: c.bg,
        color: c.text,
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: c.dot,
          animation: status === 'GREEN' ? 'pulse 2s infinite' : undefined,
        }}
      />
      {status}
    </span>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function SprintHealthPanel({
  sprintDay,
  sprintTotal,
  ptsDone,
  ptsRemaining,
  spikeCount,
  spikePts,
  healthStatus,
  totalPoints,
}: SprintHealthPanelProps) {
  const hasPlan = sprintTotal > 0;
  const sprintPct = hasPlan ? Math.round((sprintDay / sprintTotal) * 100) : 0;
  const burndownData = hasPlan ? generateBurndownData(totalPoints, sprintTotal, ptsDone, sprintDay) : [];

  // Ideal points done at this day
  const idealRate = hasPlan ? totalPoints / sprintTotal : 0;
  const idealDone = Math.round(idealRate * sprintDay);
  const vsIdeal = ptsDone - idealDone;
  const vsIdealLabel = vsIdeal >= 0 ? `+${vsIdeal} ahead` : `${vsIdeal} behind`;

  return (
    <div
      data-testid="sprint-health-panel"
      style={{
        display: 'grid',
        gridTemplateColumns: hasPlan ? '1fr auto 1fr' : '1fr 1fr',
        gap: 24,
        padding: 16,
        borderRadius: 8,
        border: '1px solid var(--border)',
        backgroundColor: 'var(--surface)',
      }}
    >
      {/* Left: Sprint progress */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
          Sprint Progress
        </div>

        {hasPlan ? (
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            Day {sprintDay}/{sprintTotal}
            <span style={{ fontSize: 13, fontWeight: 400, marginLeft: 6, color: 'var(--text-secondary, #6b7280)' }}>
              ({sprintPct}%)
            </span>
          </div>
        ) : (
          <div style={{ fontSize: 14, color: 'var(--text-secondary, #6b7280)' }} data-testid="no-sprint-plan">
            No sprint plan — generate one for timing data
          </div>
        )}

        <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
          <span data-testid="pts-done-label">
            <strong data-testid="pts-done">{ptsDone}</strong> pts done
          </span>
          <span style={{ color: 'var(--text-secondary, #6b7280)' }} data-testid="pts-remaining-label">
            {ptsRemaining} pts remaining
          </span>
        </div>

        {spikeCount > 0 && (
          <div
            data-testid="spike-indicator"
            style={{ fontSize: 13, color: '#d97706' }}
          >
            {spikeCount} spike{spikeCount !== 1 ? 's' : ''} ({spikePts} pts)
          </div>
        )}

        {hasPlan && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
            vs ideal: {vsIdealLabel}
          </div>
        )}
      </div>

      {/* Centre: Mini burndown (only with a sprint plan) */}
      {hasPlan && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <MiniChart data={burndownData} totalPoints={totalPoints} />
        </div>
      )}

      {/* Right: Health badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <HealthBadge status={healthStatus} />
      </div>
    </div>
  );
}
